import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Music, AlertCircle, Loader2, Pencil, Play, Square, RefreshCw, Search, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { SoundLibraryEffect, SoundLibraryPatch, soundLibraryService } from '../../services/soundLibraryService';
import { apiErrorMessage } from '../../services/ttsVoicesService';

const inputClass = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
const thClass = 'text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap';

const Toggle: React.FC<{ on: boolean; onClick: () => void; disabled?: boolean; label: string }> = ({ on, onClick, disabled, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={`relative inline-flex h-5 w-9 rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-primary' : 'bg-slate-200'}`}
  >
    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </button>
);

const parseTags = (raw: string) => [...new Set(raw.split(',').map(t => t.trim().toLowerCase()).filter(Boolean))];

export default function SoundLibraryAdmin() {
  const [effects, setEffects] = useState<SoundLibraryEffect[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showDisabled, setShowDisabled] = useState(true);
  const [busyName, setBusyName] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ name: string; label: string; tags: string } | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setEffects(await soundLibraryService.list());
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load sound effects'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(null);
  };

  const togglePlay = async (name: string) => {
    if (playing === name) { stop(); return; }
    stop();
    setLoadingAudio(name);
    try {
      const audio = new Audio(await soundLibraryService.audioUrl(name));
      audio.onended = () => setPlaying(p => (p === name ? null : p));
      audioRef.current = audio;
      await audio.play();
      setPlaying(name);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not play that effect'));
    } finally {
      setLoadingAudio(null);
    }
  };

  const patch = async (name: string, change: SoundLibraryPatch) => {
    setBusyName(name);
    try {
      const updated = await soundLibraryService.update(name, change);
      setEffects(prev => prev.map(e => (e.name === name ? updated : e)));
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update the effect'));
      return false;
    } finally {
      setBusyName(null);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.label.trim()) { toast.error('Label cannot be empty'); return; }
    if (await patch(editing.name, { label: editing.label.trim(), tags: parseTags(editing.tags) })) {
      setEditing(null);
      toast.success('Effect updated');
    }
  };

  const allTags = useMemo(() => [...new Set(effects.flatMap(e => e.tags))].sort(), [effects]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return effects.filter(e => (showDisabled || e.enabled)
      && (!tagFilter || e.tags.includes(tagFilter))
      && (!q || [e.name, e.label, e.prompt, ...e.tags].some(f => f.toLowerCase().includes(q))));
  }, [effects, search, tagFilter, showDisabled]);
  const enabledCount = effects.filter(e => e.enabled).length;

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Music size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Sound Effects</h1>
              <p className="text-slate-500 text-sm font-medium">
                Shared library for films, free to use over MCP. Add effects with save_sound_effect. {enabledCount} enabled of {effects.length}.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} title="Refresh" className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
              <RefreshCw size={16} />
            </button>
          </div>
        </header>

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            <AlertCircle size={16} className="shrink-0" /> {error}
          </div>
        )}

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0]">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filter by name, label, prompt, tag…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
              <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
                <option value="">All tags</option>
                {allTags.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-primary rounded" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
                Show disabled
              </label>
              <span className="ml-auto text-xs text-slate-400">{filtered.length} effect{filtered.length !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-20 text-slate-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Music size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">{effects.length ? 'No effects match' : 'The library is empty'}</p>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className={thClass} />
                    <th className={thClass}>Name</th>
                    <th className={thClass}>Label</th>
                    <th className={thClass}>Tags</th>
                    <th className={thClass}>Length</th>
                    <th className={thClass}>Prompt</th>
                    <th className={thClass}>Enabled</th>
                    <th className={thClass}>Source</th>
                    <th className={thClass} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {filtered.map(e => {
                    const busy = busyName === e.name;
                    const isEditing = editing?.name === e.name;
                    return (
                      <tr key={e.name} className={`hover:bg-slate-50/60 transition-colors ${e.enabled ? '' : 'opacity-60'}`}>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => togglePlay(e.name)}
                            title={playing === e.name ? 'Stop' : 'Play'}
                            className="flex items-center justify-center w-[30px] h-[30px] rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                          >
                            {loadingAudio === e.name ? <Loader2 size={14} className="animate-spin" /> : playing === e.name ? <Square size={12} /> : <Play size={14} />}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{e.name}</td>
                        <td className="px-4 py-3 text-navy font-medium min-w-[200px]">
                          {isEditing
                            ? <input className={inputClass} value={editing.label} onChange={ev => setEditing({ ...editing, label: ev.target.value })} />
                            : e.label}
                        </td>
                        <td className="px-4 py-3 min-w-[180px]">
                          {isEditing ? (
                            <input className={inputClass} value={editing.tags} placeholder="impact, trailer" onChange={ev => setEditing({ ...editing, tags: ev.target.value })} />
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {e.tags.map(t => (
                                <span key={t} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/30">{t}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{e.durationSec} s</td>
                        <td className="px-4 py-3 text-slate-600 max-w-[320px]">
                          <p className="truncate text-xs" title={e.prompt}>{e.prompt}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Toggle on={e.enabled} disabled={busy} onClick={() => patch(e.name, { enabled: !e.enabled })} label={e.enabled ? 'Disable effect' : 'Enable effect'} />
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{e.source}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {busy && <Loader2 size={14} className="animate-spin text-slate-400" />}
                            {isEditing ? (
                              <>
                                <button onClick={saveEdit} disabled={busy} title="Save" className="p-1.5 rounded-lg hover:bg-slate-100 text-primary">
                                  <Check size={14} />
                                </button>
                                <button onClick={() => setEditing(null)} title="Cancel" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <button onClick={() => setEditing({ name: e.name, label: e.label, tags: e.tags.join(', ') })} title="Edit label and tags" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                                <Pencil size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
